import {
  Check,
  Download,
  MessageSquare,
  Search,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { StatusPill } from '@/components/ui/StatusPill';
import { FilterPill } from '@/components/common/FilterPill';
import { Popover } from '@/components/ui/Popover';
import { EmptyState } from '@/components/common/EmptyState';
import { Pagination } from '@/components/common/Pagination';
import { useRequests, useUpdateRequestStatus } from '@/data/api';
import {
  REQUEST_TYPE_LABEL,
  type CustomerRequest,
  type RequestType,
  type Status,
} from '@/types/request';
import { REQUEST_NAV } from '@/components/layout/navItems';
import { formatKes, formatRelative, maskAccount } from '@/lib/format';
import { cn } from '@/lib/cn';
import { RequestDetailDrawer } from './RequestDetailDrawer';
import { ApproveModal, RejectModal } from './ApproveRejectModals';
import { toast } from '@/components/ui/Toast';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/common/ErrorState';

const STATUS_OPTIONS: Status[] = [
  'SUBMITTED', 'UNDER_REVIEW', 'IN_PROGRESS',
  'APPROVED', 'COMPLETED', 'REJECTED', 'CANCELLED',
];

const PAGE_SIZE = 20;

const STATUS_LABEL: Record<Status, string> = {
  SUBMITTED: 'Submitted',
  UNDER_REVIEW: 'Under review',
  IN_PROGRESS: 'In progress',
  APPROVED: 'Approved',
  COMPLETED: 'Completed',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

export function RequestsPage() {
  const params = useParams<{ section?: string }>();
  // Per-type pages mount with a path param. The route table maps each path
  // to a RequestType — we derive that lookup here so all 13 pages share one
  // component.
  const fixedType = useMemo<RequestType | null>(() => {
    if (!params.section) return null;
    const match = REQUEST_NAV.find((r) => r.path === params.section);
    return match?.type ?? null;
  }, [params.section]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Status[]>([]);
  const [typeFilter, setTypeFilter] = useState<RequestType[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [detailId, setDetailId] = useState<string | null>(null);
  const [approveId, setApproveId] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  // Hard-reset selection + filters when the route changes (e.g. switching
  // to a per-type page).
  useEffect(() => {
    setSelectedIds(new Set());
    setSearch('');
    setStatusFilter([]);
    setTypeFilter([]);
    setPage(0);
  }, [fixedType]);

  // Snap back to the first page whenever a filter changes so we don't land
  // on a now-empty page.
  useEffect(() => {
    setPage(0);
  }, [search, statusFilter, typeFilter]);

  const listQuery = useRequests({
    search,
    status: statusFilter,
    type: typeFilter,
    fixedType,
    page,
    size: PAGE_SIZE,
  });
  const filtered = listQuery.data?.content ?? [];
  const totalElements = listQuery.data?.totalElements ?? 0;
  const totalPages = listQuery.data?.totalPages ?? 0;
  const updateStatus = useUpdateRequestStatus();

  const anyFilterActive = statusFilter.length > 0 || typeFilter.length > 0 || search.length > 0;

  function toggleId(id: string) {
    setSelectedIds((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function selectAll(rows: CustomerRequest[]) {
    if (selectedIds.size === rows.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(rows.map((r) => r.id)));
  }

  function clearFilters() {
    setSearch('');
    setStatusFilter([]);
    setTypeFilter([]);
  }

  async function bulkApprove() {
    const ids = [...selectedIds];
    await Promise.allSettled(
      ids.map((id) => updateStatus.mutateAsync({ id, status: 'APPROVED' })),
    );
    toast.success(`${ids.length} requests approved`);
    setSelectedIds(new Set());
  }

  async function bulkReject() {
    const ids = [...selectedIds];
    await Promise.allSettled(
      ids.map((id) =>
        updateStatus.mutateAsync({
          id,
          status: 'REJECTED',
          note: 'Bulk rejection from back office',
        }),
      ),
    );
    toast.success(`${ids.length} requests rejected`);
    setSelectedIds(new Set());
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Filter strip */}
      <Card padding="sm" className="flex flex-wrap items-center gap-2.5">
        <Input
          containerClassName="w-72"
          placeholder="Search by ref, account, or customer"
          iconLeft={<Search className="h-4 w-4" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Popover
          trigger={
            <FilterPill
              label={
                statusFilter.length
                  ? `Status · ${statusFilter.length}`
                  : 'All statuses'
              }
              active={statusFilter.length > 0}
              onClear={statusFilter.length ? () => setStatusFilter([]) : undefined}
            />
          }
        >
          <div className="flex flex-col gap-1 min-w-[200px]">
            {STATUS_OPTIONS.map((s) => (
              <label
                key={s}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-canvas cursor-pointer"
              >
                <Checkbox
                  checked={statusFilter.includes(s)}
                  onChange={(e) => {
                    if (e.target.checked) setStatusFilter([...statusFilter, s]);
                    else setStatusFilter(statusFilter.filter((x) => x !== s));
                  }}
                />
                <span className="text-body text-ink">{STATUS_LABEL[s]}</span>
              </label>
            ))}
          </div>
        </Popover>

        {!fixedType && (
          <Popover
            trigger={
              <FilterPill
                label={
                  typeFilter.length
                    ? `Service · ${typeFilter.length}`
                    : 'All services'
                }
                active={typeFilter.length > 0}
                onClear={typeFilter.length ? () => setTypeFilter([]) : undefined}
              />
            }
          >
            <div className="flex flex-col gap-1 min-w-[240px] max-h-[300px] overflow-y-auto">
              {REQUEST_NAV.map((r) => (
                <label
                  key={r.type}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-canvas cursor-pointer"
                >
                  <Checkbox
                    checked={typeFilter.includes(r.type)}
                    onChange={(e) => {
                      if (e.target.checked) setTypeFilter([...typeFilter, r.type]);
                      else setTypeFilter(typeFilter.filter((x) => x !== r.type));
                    }}
                  />
                  <span className="text-body text-ink">{r.label}</span>
                </label>
              ))}
            </div>
          </Popover>
        )}

        <FilterPill label="Last 30 days" />
        <FilterPill label="All assignees" />

        <div className="flex-1" />

        {anyFilterActive && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-caption font-semibold text-brand-blue hover:underline px-2"
          >
            Clear all
          </button>
        )}

        <Button variant="secondary" size="sm" iconLeft={<Download className="h-3.5 w-3.5" />}>
          Export
        </Button>
      </Card>

      {/* Table */}
      <Card padding="none" className="overflow-hidden">
        {listQuery.isLoading ? (
          <div className="p-4 space-y-2">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : listQuery.isError ? (
          <ErrorState
            title="Couldn't load requests"
            description={(listQuery.error as Error)?.message ?? 'Try again.'}
            onRetry={() => listQuery.refetch()}
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No requests match your filters"
            description="Try widening the date range, or clear active filters."
            action={
              anyFilterActive ? (
                <Button variant="secondary" onClick={clearFilters}>Clear filters</Button>
              ) : undefined
            }
          />
        ) : (
          <RequestsTable
            rows={filtered}
            selectedIds={selectedIds}
            onToggle={toggleId}
            onSelectAll={() => selectAll(filtered)}
            onRowClick={(r) => setDetailId(r.id)}
            onApprove={(r) => setApproveId(r.id)}
            onReject={(r) => setRejectId(r.id)}
          />
        )}
        {!listQuery.isLoading && !listQuery.isError && totalElements > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        )}
      </Card>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 animate-slide-in-bottom">
          <div className="flex items-center gap-3 h-14 px-5 rounded-xl bg-navy text-white shadow-modal">
            <span className="text-body-strong">{selectedIds.size} selected</span>
            <span className="h-5 w-px bg-white/20 mx-1" />
            <Button size="sm" variant="success" onClick={bulkApprove} iconLeft={<Check className="h-3.5 w-3.5" />}>
              Approve
            </Button>
            <Button size="sm" variant="danger" onClick={bulkReject} iconLeft={<X className="h-3.5 w-3.5" />}>
              Reject
            </Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/10">Reassign</Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" iconLeft={<Download className="h-3.5 w-3.5" />}>
              Export
            </Button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="p-1.5 rounded hover:bg-white/10"
              aria-label="Clear selection"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <RequestDetailDrawer
        id={detailId}
        onClose={() => setDetailId(null)}
        onApprove={(id) => {
          setDetailId(null);
          setApproveId(id);
        }}
        onReject={(id) => {
          setDetailId(null);
          setRejectId(id);
        }}
      />

      <ApproveModal
        id={approveId}
        rows={filtered}
        onClose={() => setApproveId(null)}
        onConfirm={async (id, note) => {
          try {
            await updateStatus.mutateAsync({ id, status: 'APPROVED', note });
            toast.success('Request approved', 'The customer will be notified shortly.');
          } catch (e) {
            toast.error('Approve failed', (e as Error).message);
          }
          setApproveId(null);
        }}
      />

      <RejectModal
        id={rejectId}
        rows={filtered}
        onClose={() => setRejectId(null)}
        onConfirm={async (id, reason) => {
          try {
            await updateStatus.mutateAsync({ id, status: 'REJECTED', note: reason });
            toast.success('Request rejected', 'The customer will receive your reason.');
          } catch (e) {
            toast.error('Reject failed', (e as Error).message);
          }
          setRejectId(null);
        }}
      />
    </div>
  );
}

interface TableProps {
  rows: CustomerRequest[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onRowClick: (r: CustomerRequest) => void;
  onApprove: (r: CustomerRequest) => void;
  onReject: (r: CustomerRequest) => void;
}

function RequestsTable({
  rows,
  selectedIds,
  onToggle,
  onSelectAll,
  onRowClick,
  onApprove,
  onReject,
}: TableProps) {
  const allSelected = rows.length > 0 && selectedIds.size === rows.length;
  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full text-body">
        <thead className="sticky top-0 bg-white border-b border-line z-10">
          <tr className="text-overline text-ink-hint uppercase">
            <th className="w-10 px-4 py-2.5">
              <Checkbox
                checked={allSelected}
                onChange={onSelectAll}
                aria-label="Select all"
              />
            </th>
            <th className="text-left py-2.5">Ref</th>
            <th className="text-left py-2.5">Type</th>
            <th className="text-left py-2.5">Customer</th>
            <th className="text-left py-2.5">Location</th>
            <th className="text-left py-2.5">Submitted</th>
            <th className="text-left py-2.5">Status</th>
            <th className="text-right py-2.5">Amount</th>
            <th className="text-left py-2.5 pl-6">Assignee</th>
            <th className="w-32 py-2.5 px-4" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const selected = selectedIds.has(r.id);
            return (
              <tr
                key={r.id}
                className={cn(
                  'group cursor-pointer border-t border-line-soft transition-colors',
                  selected ? 'bg-status-info-bg' : 'hover:bg-canvas-hover',
                )}
                onClick={() => onRowClick(r)}
              >
                <td
                  className="px-4 py-2.5"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle(r.id);
                  }}
                >
                  <Checkbox checked={selected} onChange={() => onToggle(r.id)} />
                </td>
                <td className="py-2.5 font-mono text-caption text-navy">
                  {r.referenceNumber}
                </td>
                <td className="py-2.5 text-ink">{REQUEST_TYPE_LABEL[r.requestType]}</td>
                <td className="py-2.5">
                  <div className="text-ink">{r.contactName}</div>
                  <div className="text-caption text-ink-muted font-mono">
                    {maskAccount(r.accountNumber)}
                  </div>
                </td>
                <td className="py-2.5 text-ink-muted truncate max-w-[160px]">
                  {r.location ?? '—'}
                </td>
                <td className="py-2.5 text-ink-muted">{formatRelative(r.submittedAt)}</td>
                <td className="py-2.5"><StatusPill status={r.status} /></td>
                <td className="py-2.5 text-right font-mono tabular">
                  {r.totalAmount ? `KES ${formatKes(r.totalAmount)}` : '—'}
                </td>
                <td className="py-2.5 pl-6">
                  {r.assignee ? (
                    <div className="inline-flex items-center gap-2">
                      <Avatar name={r.assignee.name} size="xs" />
                      <span className="text-caption text-ink-muted">
                        {r.assignee.name.split(' ')[0]}
                      </span>
                    </div>
                  ) : (
                    <span className="text-caption text-ink-hint">Unassigned</span>
                  )}
                </td>
                <td className="py-2.5 px-4">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-end gap-0.5">
                    <RowAction
                      label="Approve"
                      color="text-status-success"
                      onClick={(e) => {
                        e.stopPropagation();
                        onApprove(r);
                      }}
                      icon={<Check className="h-3.5 w-3.5" />}
                    />
                    <RowAction
                      label="Reject"
                      color="text-status-error"
                      onClick={(e) => {
                        e.stopPropagation();
                        onReject(r);
                      }}
                      icon={<X className="h-3.5 w-3.5" />}
                    />
                    <RowAction
                      label="Chat"
                      color="text-brand-blue"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRowClick(r);
                      }}
                      icon={<MessageSquare className="h-3.5 w-3.5" />}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RowAction({
  label,
  icon,
  color,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  color: string;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        'inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-white',
        color,
      )}
    >
      {icon}
    </button>
  );
}
